import { ProjectLightInfoDto } from '@api/client';
import { useAppDispatch } from 'core/store/hooks';
import { useState } from 'react';
import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import AppModal from 'shared/design-system/components/app-modal';
import { deleteOrderProjectAssociation, searchActiveOrders } from '../../../_redux/actions';
import { AppToastService } from 'shared/design-system/components/app-toast/app-toast.service';
import NoData from 'shared/design-system/components/no-data-box';
import { formatCurrency } from 'shared/utils/common.utils';
import { Dropdown, MenuProps, Tooltip } from 'antd';
import { recalculateInvoicesAutomaticActiveCycle } from 'pages/private/_redux/active-cycle/actions';

interface Props {
  projects: ProjectLightInfoDto[];
  orderId: string | undefined;
  numericOrderId: number | undefined;
}

const ActiveOrderAssociatedProjectsTable: React.FC<Props> = ({ projects, orderId, numericOrderId }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [isRemoveAssociationModalVisible, setIsRemoveAssociationModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectLightInfoDto | null>(null);

  const handleRemoveAssociation = (project: ProjectLightInfoDto) => {
    const { projectId, intranetId, projectName } = project;
    if (!projectId || !orderId || !numericOrderId) return;
    dispatch(deleteOrderProjectAssociation({ orderId: numericOrderId, projectId })).then(() => {
      setSelectedProject(null);
      AppToastService.success(t(`Project (${intranetId}) ${projectName} removed successfully`));
      dispatch(searchActiveOrders({ contractOrderSearchDto: { cicloAttivoOrderNumber: orderId } }));
    });
  };

  const handleMenuClick: (project: ProjectLightInfoDto) => MenuProps['onClick'] = (project) => (e) => {
    switch (e.key) {
      case '1':
        if (!numericOrderId || !project.projectId) return;
        dispatch(recalculateInvoicesAutomaticActiveCycle({ projectId: project.projectId, idOrdine: numericOrderId }));
        break;
      case '2':
        setSelectedProject(project);
        setIsRemoveAssociationModalVisible(true);
        break;
    }
  };

  const generateMenuItems = () => {
    return [
      // {
      //   label: t('Recalculate automatic'),
      //   key: '1',
      //   danger: false,
      //   icon: <i className="icon-work-in-progress text-xl" />,
      // },
      {
        label: t('Remove association'),
        key: '2',
        danger: true,
        icon: <i className="icon-delete text-xl" />,
      },
    ];
  };

  const menuProps = (project: ProjectLightInfoDto) => ({
    items: generateMenuItems(),
    onClick: handleMenuClick(project),
  });

  if (projects.length) {
    return (
      <div className="table-responsive table-bordered mt-3">
        <Table className={'mt-0 mb-0'}>
          <thead className="bg-red">
            <tr>
              <th scope="col">
                <span>{'Project'}</span>
              </th>
              <th scope="col">
                <span>{'Tot. Amount'}</span>
              </th>
              <th scope="col">
                <span>{'A. Revenues'}</span>
              </th>
              <th scope="col">
                <span>{'Invoices issued'}</span>
              </th>
              <th scope="col">
                <span>{'PL'}</span>
              </th>
              <th scope="col" style={{ maxWidth: '50px' }}>
                <span>{'Actions'}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => (
              <tr key={project.projectId}>
                <td>
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">{`(${project.intranetId}) ${project.projectName}`}</p>
                  </span>
                </td>
                <td className="text-end">
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">
                      {project.totAmount !== undefined ? `${formatCurrency(project.totAmount)}€` : '-'}
                    </p>
                  </span>
                </td>
                <td className="text-end">
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">
                      {project.actualRevenue !== undefined ? `${formatCurrency(project.actualRevenue)}€` : '-'}
                    </p>
                  </span>
                </td>
                <td className="text-end">
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">
                      {project.invoicedIssue !== undefined ? `${formatCurrency(project.invoicedIssue)}€` : '-'}
                    </p>
                  </span>
                </td>
                <td>
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">{project.denominazioneResponsabileProgetto || '-'}</p>
                  </span>
                </td>
                <td className="text-center">
                  <Tooltip title={'Actions'} color="white" key={`associate-button-${project.projectId}`}>
                    <Dropdown menu={menuProps(project)} trigger={['click']}>
                      <i
                        className="fa-solid fa-ellipsis-vertical text-blue-links"
                        role="button"
                        onClick={(e) => e.preventDefault()}
                      />
                    </Dropdown>
                  </Tooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <AppModal
          setShow={setIsRemoveAssociationModalVisible}
          show={isRemoveAssociationModalVisible}
          title="Remove association"
          cancelText={t('common.cancel')}
          confirmText={t('common.confirm')}
          onCancel={() => setIsRemoveAssociationModalVisible(false)}
          onConfirm={() => {
            handleRemoveAssociation(selectedProject!);
            setIsRemoveAssociationModalVisible(false);
          }}
        >
          <p>{'Are you sure you want to remove this association?'}</p>
        </AppModal>
      </div>
    );
  } else {
    return (
      <div className="d-flex justify-content-center mt-3">
        <NoData msg={t('No associated projects')} />
      </div>
    );
  }
};

export default ActiveOrderAssociatedProjectsTable;
